import { useState, useEffect, useCallback, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { providerApiClient, type RegisterProviderData } from '@/lib/api'
import type { Provider } from '@/types/provider'
import { ProviderAuthContext, type ProviderAuthContextType } from './ProviderAuthContext'
import { useTokenRefresh } from './useTokenRefresh'

interface ProviderAuthProviderProps {
  children: ReactNode
}

// Survives full-page reloads (e.g., the Stripe-step `window.location.assign`
// redirect) so the auth context can rehydrate without depending on
// `/auth/me` — which is still intercepted by dev MSW and would 401 against
// our real-backend cookies.
const PROVIDER_STORAGE_KEY = '__determa_provider__'

function loadStoredProvider(): Provider | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem(PROVIDER_STORAGE_KEY)
    return raw ? (JSON.parse(raw) as Provider) : null
  } catch {
    return null
  }
}

function persistProvider(provider: Provider | null): void {
  if (typeof window === 'undefined') return
  try {
    if (provider) {
      window.localStorage.setItem(PROVIDER_STORAGE_KEY, JSON.stringify(provider))
    } else {
      window.localStorage.removeItem(PROVIDER_STORAGE_KEY)
    }
  } catch {
    // localStorage unavailable (private mode, quota) — non-fatal.
  }
}

export function ProviderAuthProvider({ children }: ProviderAuthProviderProps) {
  const queryClient = useQueryClient()
  const [provider, setProviderState] = useState<Provider | null>(loadStoredProvider)
  const [isLoading, setIsLoading] = useState(true)

  const setProvider = useCallback((next: Provider | null) => {
    setProviderState(next)
    persistProvider(next)
  }, [])

  const handleAuthError = useCallback(() => {
    setProvider(null)
  }, [setProvider])

  useTokenRefresh({
    isAuthenticated: !!provider,
    onRefreshError: (error) => {
      console.error('Proactive token refresh failed:', error)
    },
  })

  useEffect(() => {
    providerApiClient.setAuthErrorHandler(handleAuthError)

    const checkAuth = async () => {
      try {
        const currentUser = await providerApiClient.getCurrentUser()
        setProvider(currentUser)
      } catch (_error) {
        // If `/auth/me` 401s but localStorage still has a Provider, keep
        // it — real cookies likely valid, the 401 was a dev-mock blip.
        if (!loadStoredProvider()) {
          setProvider(null)
        }
      }
      setIsLoading(false)
    }

    checkAuth()
  }, [handleAuthError, setProvider])

  const login = async (emailOrUsername: string, password: string): Promise<void> => {
    setIsLoading(true)
    try {
      await providerApiClient.login(emailOrUsername, password)
      const currentUser = await providerApiClient.getCurrentUser()
      setProvider(currentUser)
    } finally {
      setIsLoading(false)
    }
  }

  const register = async (data: RegisterProviderData): Promise<void> => {
    setIsLoading(true)
    try {
      // `/provider/onboarding/start` returns a complete `Provider` so we
      // can hydrate the auth context without a follow-up `/auth/me` call.
      // The dictation `/auth/me` endpoint returns a different shape and
      // would 401 in dev MSW (which still intercepts `/auth/*`).
      const newProvider = await providerApiClient.register(data)
      setProvider(newProvider)
    } finally {
      setIsLoading(false)
    }
  }

  const logout = async () => {
    setIsLoading(true)
    try {
      await providerApiClient.logout()
    } finally {
      setProvider(null)
      queryClient.clear()
      setIsLoading(false)
    }
  }

  const refreshProvider = async () => {
    if (!provider) return
    try {
      const currentUser = await providerApiClient.getCurrentUser()
      setProvider(currentUser)
    } catch (error) {
      console.error('Failed to refresh provider:', error)
    }
  }

  const value: ProviderAuthContextType = {
    provider,
    login,
    register,
    logout,
    refreshProvider,
    isLoading,
    isAuthenticated: !!provider,
  }

  return (
    <ProviderAuthContext.Provider value={value}>
      {children}
    </ProviderAuthContext.Provider>
  )
}
